import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import InsufficientCreditsModal from "@/Components/Common/InsufficientCreditsModal";

/**
 * Credit System Configuration
 * Must match server/backend/helpers/creditConfig.js
 */
export const FEATURE_CREDITS = {
  resume_parsing: 10,
  resume_scoring: 10,
  blank_resume: 10,
  resume_from_job_post: 15,
  ai_enhance: 5,
  cover_letter: 10,
};

export const FEATURE_DISPLAY_NAMES = {
  resume_parsing: "Resume Parsing",
  resume_scoring: "Resume Scoring",
  blank_resume: "Blank Resume",
  resume_from_job_post: "Resume from Job Post",
  ai_enhance: "AI Enhancement",
  cover_letter: "Cover Letter",
};

const CreditContext = createContext();

export const CreditProvider = ({ children }) => {
  const fetch = useAuthenticatedFetch();

  // Credit state
  const [creditInfo, setCreditInfo] = useState({
    balance: 0,
    totalPurchased: 0,
    totalUsed: 0,
    freeCreditsGiven: 30,
    lastUpdated: null,
    loading: true,
  });

  // Modal state for insufficient credits
  const [insufficientModal, setInsufficientModal] = useState({
    visible: false,
    requiredCredits: 0,
    availableCredits: 0,
    featureName: "",
    feature: "",
  });

  /**
   * Check if user is authenticated (has both userId and token)
   */
  const isAuthenticated = useCallback(() => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");
    return !!(userId && token);
  }, []);

  /**
   * Fetch credit balance from API
   */
  const refreshCredits = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated()) {
      setCreditInfo((prev) => ({ ...prev, loading: false }));
      return;
    }

    try {
      const response = await fetch.get("/credits/balance");

      if (response.success) {
        setCreditInfo({
          balance: response.data.balance ?? 0,
          totalPurchased: response.data.totalPurchased ?? 0,
          totalUsed: response.data.totalUsed ?? 0,
          freeCreditsGiven: response.data.freeCreditsGiven ?? 30,
          lastUpdated: new Date(),
          loading: false,
          // Feature costs from server
          featureCosts: response.data.featureCosts || FEATURE_CREDITS,
          featureNames: response.data.featureNames || FEATURE_DISPLAY_NAMES,
          // Convenience flags
          canCreateResume: response.data.canCreateResume,
          canCreateBlankResume: response.data.canCreateBlankResume,
          canEnhance: response.data.canEnhance,
          canGenerateCoverLetter: response.data.canGenerateCoverLetter,
          canScoreResume: response.data.canScoreResume,
          canGenerateFromJobPost: response.data.canGenerateFromJobPost,
        });
      } else {
        // Handle non-success response
        setCreditInfo((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      // Handle 403 (invalid token) silently - don't retry
      if (error?.response?.status === 403 || error?.code === 403) {
        // console.log("Credit fetch skipped - invalid or expired token");
      } else {
        console.error("Error fetching credit balance:", error);
      }
      setCreditInfo((prev) => ({ ...prev, loading: false }));
    }
  }, [fetch, isAuthenticated]);

  // Fetch credits on mount
  useEffect(() => {
    if (isAuthenticated()) {
      refreshCredits();
    } else {
      setCreditInfo((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  /**
   * Check if user has enough credits for a feature
   * Returns true if enough credits, false otherwise
   *
   * @param {string} feature - Feature key from FEATURE_CREDITS
   * @param {boolean} showModal - Whether to show modal if insufficient (default: true)
   * @returns {boolean}
   */
  const hasEnoughCredits = useCallback(
    (feature, showModal = true) => {
      const requiredCredits = creditInfo.featureCosts?.[feature] ?? FEATURE_CREDITS[feature];
      const availableCredits = creditInfo.balance;

      if (availableCredits >= requiredCredits) {
        return true;
      }

      if (showModal) {
        setInsufficientModal({
          visible: true,
          requiredCredits,
          availableCredits,
          featureName: creditInfo.featureNames?.[feature] ?? FEATURE_DISPLAY_NAMES[feature] ?? feature,
          feature,
        });
      }

      return false;
    },
    [creditInfo]
  );

  /**
   * Execute a credit-consuming action with automatic guard
   * Checks credits first, executes action only if sufficient
   *
   * @param {string} feature - Feature key
   * @param {Function} action - Async function to execute if credits are sufficient
   * @param {object} options - Additional options
   * @returns {Promise<any>} Result of action or null if blocked
   */
  const executeWithCreditGuard = useCallback(
    async (feature, action, options = {}) => {
      const { showModal = true, onInsufficientCredits } = options;

      // Check credits first (frontend guard)
      if (!hasEnoughCredits(feature, showModal)) {
        if (onInsufficientCredits) {
          onInsufficientCredits();
        }
        return { success: false, error: "INSUFFICIENT_CREDITS" };
      }

      try {
        // Execute the action
        const result = await action();

        // If the action was successful and returned data, refresh credits
        // This ensures balance is updated after successful operations
        if (result?.success !== false) {
          // Refresh credits in background
          setTimeout(() => refreshCredits(), 500);
        }

        return result;
      } catch (error) {
        // Check if error is due to insufficient credits from backend
        if (error.response?.status === 402 || error.response?.data?.code === "INSUFFICIENT_CREDITS") {
          const data = error.response?.data?.data || {};

          if (showModal) {
            setInsufficientModal({
              visible: true,
              requiredCredits: data.required ?? FEATURE_CREDITS[feature],
              availableCredits: data.available ?? creditInfo.balance,
              featureName: data.featureName ?? FEATURE_DISPLAY_NAMES[feature] ?? feature,
              feature,
            });
          }

          // Refresh credits to sync with server
          refreshCredits();

          return { success: false, error: "INSUFFICIENT_CREDITS", data };
        }

        throw error;
      }
    },
    [hasEnoughCredits, creditInfo, refreshCredits]
  );

  /**
   * Update credits locally (for optimistic UI updates)
   * Should be called after successful credit deduction
   *
   * @param {number} amount - Amount to deduct (positive number)
   */
  const deductCreditsLocally = useCallback((amount) => {
    setCreditInfo((prev) => ({
      ...prev,
      balance: Math.max(0, (prev.balance ?? 0) - amount),
      totalUsed: (prev.totalUsed ?? 0) + amount,
      lastUpdated: new Date(),
    }));
  }, []);

  /**
   * Add credits locally (for optimistic UI after purchase)
   *
   * @param {number} amount - Amount to add
   */
  const addCreditsLocally = useCallback((amount) => {
    setCreditInfo((prev) => ({
      ...prev,
      balance: (prev.balance ?? 0) + amount,
      totalPurchased: (prev.totalPurchased ?? 0) + amount,
      lastUpdated: new Date(),
    }));
  }, []);

  /**
   * Close the insufficient credits modal
   */
  const closeInsufficientModal = useCallback(() => {
    setInsufficientModal((prev) => ({ ...prev, visible: false }));
  }, []);

  /**
   * Get the cost of a feature
   *
   * @param {string} feature - Feature key
   * @returns {number}
   */
  const getFeatureCost = useCallback(
    (feature) => {
      return creditInfo.featureCosts?.[feature] ?? FEATURE_CREDITS[feature] ?? 0;
    },
    [creditInfo]
  );

  /**
   * Check if a specific feature is available based on credits
   *
   * @param {string} feature - Feature key
   * @returns {boolean}
   */
  const isFeatureAvailable = useCallback(
    (feature) => {
      const cost = getFeatureCost(feature);
      return creditInfo.balance >= cost;
    },
    [creditInfo, getFeatureCost]
  );

  const value = {
    // State
    creditInfo,
    balance: creditInfo.balance,
    loading: creditInfo.loading,

    // Feature costs
    featureCosts: creditInfo.featureCosts || FEATURE_CREDITS,
    featureNames: creditInfo.featureNames || FEATURE_DISPLAY_NAMES,
    FEATURE_CREDITS,
    FEATURE_DISPLAY_NAMES,

    // Methods
    refreshCredits,
    hasEnoughCredits,
    executeWithCreditGuard,
    deductCreditsLocally,
    addCreditsLocally,
    getFeatureCost,
    isFeatureAvailable,

    // Modal control
    showInsufficientModal: (feature) => hasEnoughCredits(feature, true),
    closeInsufficientModal,
  };

  return (
    <CreditContext.Provider value={value}>
      {children}

      {/* Global Insufficient Credits Modal */}
      <InsufficientCreditsModal
        visible={insufficientModal.visible}
        onClose={closeInsufficientModal}
        requiredCredits={insufficientModal.requiredCredits}
        availableCredits={insufficientModal.availableCredits}
        featureName={insufficientModal.featureName}
      />
    </CreditContext.Provider>
  );
};

/**
 * Custom hook to use credit context
 */
export const useCredits = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditProvider");
  }
  return context;
};

/**
 * Custom hook for credit-guarded actions
 * Returns a function that checks credits before executing
 *
 * @param {string} feature - Feature key
 * @returns {Function} Guarded action executor
 */
export const useCreditGuard = (feature) => {
  const { executeWithCreditGuard, getFeatureCost, isFeatureAvailable } = useCredits();

  const execute = useCallback(
    (action, options = {}) => executeWithCreditGuard(feature, action, options),
    [feature, executeWithCreditGuard]
  );

  return {
    execute,
    cost: getFeatureCost(feature),
    isAvailable: isFeatureAvailable(feature),
  };
};

export default CreditContext;
